import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useNotifications } from "../../contexts/NotificationContext";
import {
  ArrowLeft,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  DollarSign,
  FileText,
  Calendar,
  User,
  Edit,
  Send,
  Eye,
  Download,
  Star,
} from "lucide-react";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import toast from "react-hot-toast";

const AdminProjectDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const { currentUser, userProfile } = useAuth();
  const { createNotification } = useNotifications();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState("");
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [pricingData, setPricingData] = useState({
    price: "",
    notes: "",
  });
  const [deliveryData, setDeliveryData] = useState({
    projectLink: "",
    notes: "",
  });

  useEffect(() => {
    fetchProject();
    if (location.state && location.state.openDeliveryModal) {
      setShowDeliveryModal(true);
    }
  }, [id]);

  const fetchProject = async () => {
    try {
      const projectRef = doc(db, "projects", id);
      const projectSnap = await getDoc(projectRef);

      if (projectSnap.exists()) {
        const projectData = projectSnap.data();
        setProject({
          id: projectSnap.id,
          ...projectData,
        });
        setAdminNotes(projectData.adminNotes || "");
      } else {
        toast.error("Project not found");
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Failed to load project details");
    } finally {
      setLoading(false);
    }
  };

  const updateProjectStatus = async (newStatus) => {
    try {
      const projectRef = doc(db, "projects", id);
      const updateData = {
        status: newStatus,
        updatedAt: serverTimestamp(),
      };
      if (newStatus === "in-progress") {
        updateData.paymentStatus = "done";
        updateData.startedAt = serverTimestamp();
      }
      await updateDoc(projectRef, updateData);

      // Send notification to client
      await createNotification(
        project.clientId,
        "Project Status Updated",
        `Your project "${
          project.title
        }" status has been updated to ${newStatus.replace("-", " ")}.`,
        "info",
        project.id
      );

      toast.success("Project status updated successfully");
      fetchProject();
    } catch (error) {
      console.error("Error updating project status:", error);
      toast.error("Failed to update project status");
    }
  };

  const handlePricingSubmit = async (e) => {
    e.preventDefault();

    if (!pricingData.price || parseFloat(pricingData.price) <= 0) {
      toast.error("Please enter a valid price");
      return;
    }

    try {
      const projectRef = doc(db, "projects", id);
      await updateDoc(projectRef, {
        totalAmount: parseFloat(pricingData.price),
        status: "approved",
        adminNotes: pricingData.notes,
        updatedAt: serverTimestamp(),
      });

      // Send notification to client
      await createNotification(
        project.clientId,
        "Project Approved",
        `Your project "${project.title}" has been approved with a price of ₹${pricingData.price}. Please complete the payment to start work.`,
        "success",
        project.id
      );

      toast.success("Project approved and priced successfully");
      setShowPricingModal(false);
      setPricingData({ price: "", notes: "" });
      fetchProject();
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error("Failed to update project");
    }
  };

  const handleDeliverySubmit = async (e) => {
    e.preventDefault();

    if (!deliveryData.projectLink.trim()) {
      toast.error("Please enter the project link");
      return;
    }

    try {
      const projectRef = doc(db, "projects", id);
      await updateDoc(projectRef, {
        status: "delivered",
        projectLink: deliveryData.projectLink,
        deliveryNotes: deliveryData.notes,
        deliveredAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Send notification to client
      await createNotification(
        project.clientId,
        "Project Delivered",
        `Your project "${project.title}" has been delivered! Check the project link in your project details.`,
        "success",
        project.id
      );

      toast.success("Project delivered successfully");
      setShowDeliveryModal(false);
      setDeliveryData({ projectLink: "", notes: "" });
      fetchProject();
    } catch (error) {
      console.error("Error delivering project:", error);
      toast.error("Failed to deliver project");
    }
  };

  const saveAdminNotes = async () => {
    try {
      const projectRef = doc(db, "projects", id);
      await updateDoc(projectRef, {
        adminNotes: adminNotes,
        updatedAt: serverTimestamp(),
      });

      toast.success("Admin notes saved successfully");
    } catch (error) {
      console.error("Error saving admin notes:", error);
      toast.error("Failed to save admin notes");
    }
  };

  const handleRejectProject = async () => {
    if (!window.confirm("Are you sure you want to reject this project?"))
      return;
    try {
      const projectRef = doc(db, "projects", id);
      await updateDoc(projectRef, {
        status: "rejected",
        updatedAt: serverTimestamp(),
      });
      await createNotification(
        project.clientId,
        "Project Rejected",
        `Your project "${project.title}" has been rejected by the admin. Please contact support for more information.`,
        "error",
        project.id
      );
      toast.success("Project rejected successfully");
      fetchProject();
    } catch (error) {
      console.error("Error rejecting project:", error);
      toast.error("Failed to reject project");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock className="h-6 w-6 text-warning-500" />;
      case "approved":
        return <CheckCircle className="h-6 w-6 text-success-500" />;
      case "in-progress":
        return <AlertCircle className="h-6 w-6 text-primary-500" />;
      case "completed":
        return <CheckCircle className="h-6 w-6 text-success-500" />;
      case "delivered":
        return <CheckCircle className="h-6 w-6 text-success-500" />;
      default:
        return <Clock className="h-6 w-6 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: "status-pending",
      approved: "status-approved",
      "in-progress": "status-in-progress",
      completed: "status-completed",
      delivered: "status-delivered",
      rejected: "status-rejected",
      cancelled: "status-cancelled",
    };

    return (
      <span className={statusClasses[status] || "status-pending"}>
        {status.replace("-", " ").toUpperCase()}
      </span>
    );
  };

  const getStatusActions = () => {
    switch (project.status) {
      case "pending":
        return (
          <>
            <button
              onClick={() => {
                setShowPricingModal(true);
              }}
              className="btn-primary"
            >
              <Edit className="h-4 w-4 mr-2" />
              Set Price & Approve
            </button>
            <button onClick={handleRejectProject} className="btn-danger ml-2">
              Reject Project
            </button>
          </>
        );
      case "approved":
        if (
          ["paid", "pending_verification", "done"].includes(
            project.paymentStatus
          )
        ) {
          return (
            <button
              onClick={() => updateProjectStatus("in-progress")}
              className="btn-success"
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Start Development
            </button>
          );
        }
        return null;
      case "in-progress":
        return (
          <button
            onClick={() => updateProjectStatus("completed")}
            className="btn-success"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Mark as Completed
          </button>
        );
      case "completed":
        return (
          <button
            onClick={() => setShowDeliveryModal(true)}
            className="btn-success"
          >
            <Download className="h-4 w-4 mr-2" />
            Deliver to Client
          </button>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Project not found</h3>
        <p className="text-gray-500 mt-2">
          The project you're looking for doesn't exist.
        </p>
        <Link to="/admin/projects" className="btn-primary mt-4">
          Back to Projects
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen mt-12 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/admin/projects" className="btn-outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Projects
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {project.title}
            </h1>
            <p className="text-gray-600">Project ID: {project.id}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {getStatusBadge(project.status)}
          <Link
            to={`/admin/chat/${project.id}`}
            className="btn-outline btn-sm w-full flex items-center justify-center"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            Chat with Client
          </Link>
        </div>
      </div>

      {/* Project Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status Card */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(project.status)}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Project Status
                    </h3>
                    <p className="text-sm text-gray-500">
                      Current status: {project.status.replace("-", " ")}
                    </p>
                  </div>
                </div>
                {getStatusActions()}
              </div>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Project Description
                  </label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {project.description || "No description provided"}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Requirements
                  </label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {project.requirements || "No requirements specified"}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Categories
                    </label>
                    <div className="mt-1">
                      {project.categories && project.categories.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {project.categories.map((cat, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary-100 text-primary-800"
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-900">
                          {project.category || "Not specified"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Delivery Speed
                    </label>
                    <p className="mt-1 text-sm text-gray-900">
                      {(() => {
                        if (project.deliverySpeed === "1_day")
                          return "1 Day Delivery (+₹100)";
                        if (project.deliverySpeed === "3_days")
                          return "3 Day Delivery (+₹50)";
                        return "1 Week Delivery (Free)";
                      })()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Payment Breakdown
                    </label>
                    <div className="mt-1 text-sm text-gray-900">
                      <div>
                        Base Price: ₹
                        {project.totalAmount?.toLocaleString() || "0"}
                      </div>
                      <div>
                        Delivery Extra: ₹
                        {project.deliveryExtra ? project.deliveryExtra : 0}
                      </div>
                      <div className="font-semibold">
                        Final Amount: ₹
                        {(
                          (project.totalAmount || 0) +
                          (project.deliveryExtra || 0)
                        ).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Created Date
                    </label>
                    <p className="mt-1 text-sm text-gray-900">
                      {project.createdAt
                        ? new Date(
                            project.createdAt.toDate()
                          ).toLocaleDateString()
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Admin Notes */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Admin Notes</h3>
            </div>
            <div className="card-body">
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add internal notes about this project..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows="4"
              />
              <div className="mt-3 flex justify-end">
                <button onClick={saveAdminNotes} className="btn-primary btn-sm">
                  <Edit className="h-4 w-4 mr-1" />
                  Save Notes
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Client Information */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">
                Client Information
              </h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {project.clientName?.charAt(0) || "C"}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {project.clientName}
                    </p>
                    <p className="text-sm text-gray-500">
                      {project.clientEmail}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500">
                      College
                    </label>
                    <p className="text-sm text-gray-900">
                      {project.college || "Not specified"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500">
                      Course
                    </label>
                    <p className="text-sm text-gray-900">
                      {project.course} • {project.year}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Project Details */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">
                Project Details
              </h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500">
                    Payment Breakdown
                  </label>
                  <div className="text-sm text-gray-900">
                    <div>
                      Base Price: ₹
                      {project.totalAmount?.toLocaleString() || "0"}
                    </div>
                    <div>
                      Delivery Extra: ₹
                      {project.deliveryExtra ? project.deliveryExtra : 0}
                    </div>
                    <div className="font-semibold">
                      Final Amount: ₹
                      {(
                        (project.totalAmount || 0) +
                        (project.deliveryExtra || 0)
                      ).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500">
                    Payment Status
                  </label>
                  <p className="text-sm text-gray-900">
                    {project.paymentStatus || "Pending"}
                  </p>
                </div>

                {project.paymentProof && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500">
                      Payment Proof
                    </label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {project.paymentProof}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">
                Quick Actions
              </h3>
            </div>
            <div className="card-body">
              <div className="space-y-2">
                <Link
                  to={`/admin/chat/${project.id}`}
                  className="btn-outline btn-sm w-full flex items-center justify-center"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Chat with Client
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showDeliveryModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Deliver Project
              </h3>
              <form onSubmit={handleDeliverySubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="projectLink"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Project Link
                  </label>
                  <input
                    type="url"
                    id="projectLink"
                    value={deliveryData.projectLink}
                    onChange={(e) =>
                      setDeliveryData({
                        ...deliveryData,
                        projectLink: e.target.value,
                      })
                    }
                    className="input mt-1"
                    placeholder="Enter project delivery link (Google Drive, GitHub, etc.)"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="notes"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Notes (Optional)
                  </label>
                  <textarea
                    id="notes"
                    value={deliveryData.notes}
                    onChange={(e) =>
                      setDeliveryData({
                        ...deliveryData,
                        notes: e.target.value,
                      })
                    }
                    className="input mt-1"
                    rows="3"
                    placeholder="Any notes for the client..."
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowDeliveryModal(false)}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Deliver
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {showPricingModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Set Price & Approve Project
              </h3>
              <form onSubmit={handlePricingSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="price"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Amount (₹)
                  </label>
                  <input
                    type="number"
                    id="price"
                    value={pricingData.price}
                    onChange={(e) =>
                      setPricingData({ ...pricingData, price: e.target.value })
                    }
                    className="input mt-1"
                    placeholder="Enter project amount"
                    min="0"
                    step="any"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="notes"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Admin Notes (Optional)
                  </label>
                  <textarea
                    id="notes"
                    value={pricingData.notes}
                    onChange={(e) =>
                      setPricingData({ ...pricingData, notes: e.target.value })
                    }
                    className="input mt-1"
                    rows="3"
                    placeholder="Any notes for the client..."
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPricingModal(false);
                      setPricingData({ price: "", notes: "" });
                    }}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Approve & Price
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProjectDetail;
